import { extract_error_message } from '@/utils/api_response_helpers';
import {
  buildAssignedQuestionIds,
  canToggleExistingQuestionAssignment,
  ensureUnassignSucceeded,
  finalizeSuccessfulUnassign,
  findQuestionInSection,
  findSectionById,
  hasAssignmentContext,
  loadAssignableQuestions,
  normalizeAssignmentFallback,
  performUnassignRequest,
  resolveAssignmentToggleContext,
  resolveSectionQuestionId,
  resolveUnassignPayload,
  setQuestionUnassigningState,
  toggleQuestionAssignment,
} from './create_sections_assignment_helpers.js';

function resolveQuestionId(question) {
  const question_id = Number(question?.question_id ?? question?.backend_id ?? question?.id ?? null);
  return question_id;
}

function resolveSectionId(section) {
  const section_id = Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null);
  return section_id;
}

function resolveSection(ctx, section_ref) {
  const section = section_ref && typeof section_ref === 'object'
    ? section_ref
    : ctx.sections[Number(section_ref)] || null;
  return section;
}

export const create_sections_assignment_methods = {
  async open_assign_existing_question_modal(section_ref) {
    const section = resolveSection(this, section_ref);
    const section_id = resolveSectionId(section);
    const form_id = Number(this.topBar.formId);

    if (!hasAssignmentContext(section, section_id, form_id)) {
      this.show_error('Please select a valid section first.');
      return;
    }

    this.active_assign_section = section;
    this.assign_existing_question_modal_open = true;
    this.is_loading_questions = true;
    this.question_search = '';
    this.reset_pagination();

    const assigned_question_ids = buildAssignedQuestionIds(section, resolveQuestionId);
    await loadAssignableQuestions(this, form_id, assigned_question_ids);
  },

  close_assign_existing_question_modal() {
    this.assign_existing_question_modal_open = false;
    this.active_assign_section = null;
    this.question_search = '';
    this.question_list = [];
    this.reset_pagination();
  },

  async toggle_existing_question_assignment(question) {
    const toggle_context = resolveAssignmentToggleContext(this, question, resolveQuestionId, resolveSectionId);
    if (!canToggleExistingQuestionAssignment(this, question, toggle_context)) {
      return;
    }

    question.is_saving = true;

    try {
      await toggleQuestionAssignment(this, question, toggle_context);
    } catch (error) {
      const fallback = normalizeAssignmentFallback(question);
      const message = extract_error_message(error, fallback);
      this.show_error(this.normalize_error_message(message, fallback));
    } finally {
      question.is_saving = false;
    }
  },

  question_matches_assignment(question = {}, question_id, section_question_id = null) {
    const normalized_question_id = Number(question_id);
    const normalized_section_question_id = Number(section_question_id);
    const candidate_question_id = resolveQuestionId(question);
    const candidate_section_question_id = resolveSectionQuestionId(question);

    if (normalized_section_question_id && candidate_section_question_id) {
      const is_section_question_match = candidate_section_question_id === normalized_section_question_id;
      return is_section_question_match;
    }

    const is_question_match = Boolean(normalized_question_id && candidate_question_id === normalized_question_id);
    return is_question_match;
  },

  find_section_question(section_id, question_id, section_question_id = null) {
    const normalized_section_id = Number(section_id);
    if (!normalized_section_id) {
      return null;
    }

    const section = findSectionById(this.sections, normalized_section_id, resolveSectionId);
    const matched_question = findQuestionInSection(this, section, question_id, section_question_id);
    return matched_question;
  },

  async unassign_question(section_ref, question_ref) {
    const payload = resolveUnassignPayload(
      this,
      section_ref,
      question_ref,
      resolveSection,
      resolveQuestionId,
      resolveSectionId
    );
    if (!payload) {
      return false;
    }

    setQuestionUnassigningState(this, payload, true);
    const loader = await this.show_loader('Unassigning question...');

    try {
      const response = await performUnassignRequest(payload);
      ensureUnassignSucceeded(response);
      await finalizeSuccessfulUnassign(this, payload, response);
      return true;
    } catch (error) {
      this.show_error(extract_error_message(error, 'Failed to unassign question.'));
      setQuestionUnassigningState(this, payload, false);
      return false;
    } finally {
      await this.close_loader(loader);
    }
  },
};
